import React from 'react';
import styled from 'styled-components';
import { Body5, Body7, Container } from '@ui';
import { TransparentFlatButton } from '@ui/buttons';
import { SpecEditorContext } from './SpecEditorContext';
import { problemsForPanel, countErrors } from './validate';
import { RawPane } from './RawPane';
import { Chip, ModeToggle } from './styles';

// The chrome around one of the four questions.
//
// Two things here are load-bearing and easy to get wrong:
//
//   1. The panel is COLLAPSED by default and its header carries a full plain-English summary. The
//      dominant task is duplicating a spec and changing three fields; four one-line summaries read
//      faster than four open forms, and the summary is also the pre-lock sanity read.
//
//   2. The `{ }` mode toggle is a FIRST-CLASS choice, always available — not a fallback for a
//      value the form cannot render. And the error count in the header renders in BOTH modes,
//      because an escape hatch that stops being validated is an escape hatch that quietly reads
//      as "checked".

const Panel = styled.section`
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 7px;
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  overflow: hidden;

  /* Panels are components in a stack, not sections. */
  & + & {
    margin-top: 16px;
  }
`;

const Head = styled.button`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  width: 100%;
  text-align: left;
  background: transparent;
  border: 0;
  cursor: pointer;
  padding: 16px;
  color: ${({ theme }) => theme.themeColors.text};

  &:hover {
    background: ${({ theme }) => theme.themeColors.lowContrastBorder};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
    outline-offset: -2px;
  }
`;

const Num = styled.span`
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  margin-top: 1px;
  display: grid;
  place-items: center;
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  border-radius: 4px;
  font-size: 11px;
  font-family: monospace;
  color: ${({ theme }) => theme.themeColors.secondaryText};
`;

const HeadText = styled.span`
  flex: 1;
  min-width: 0;
`;

const HeadMeta = styled.span`
  flex: 0 0 auto;
  display: flex;
  gap: 8px;
  align-items: center;
  padding-left: 8px;
`;

const Caret = styled.span`
  flex: 0 0 auto;
  margin-top: 3px;
  font-size: 11px;
  color: ${({ theme }) => theme.themeColors.secondaryText};
  transform: rotate(${({ $open }) => ($open ? '90deg' : '0deg')});
  transition: transform 0.16s ease;
`;

const Bodywrap = styled.div`
  padding: 0 16px 16px;
  border-top: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
`;

export const QuestionPanel = ({ index, panel, title, sectionKey, summary, onRevertSection, children }) => {
  const { problems, expandedPanels, togglePanel, panelModes, setPanelMode, changesBySection, seedOrigin, activePath } =
    React.useContext(SpecEditorContext);

  const bodyRef = React.useRef(null);

  const open = !!expandedPanels[panel];
  const mode = panelModes[panel] || 'form';
  const owned = problemsForPanel(problems, panel);
  const errors = countErrors(owned);
  const changed = (changesBySection[sectionKey] || []).length;

  // [Go] in the Problems strip sets activePath and expands the owning panel. Without this effect
  // it would expand a panel somewhere below the fold and leave the operator to find it — so the
  // panel scrolls itself into view and puts focus on its first control.
  const owns = !!activePath && problems.some((p) => p.path === activePath && p.panel === panel);
  React.useEffect(() => {
    if (!owns || !open || !bodyRef.current) return;
    // jsdom (and older browsers) do not implement scrollIntoView; focus alone still works.
    if (typeof bodyRef.current.scrollIntoView === 'function') bodyRef.current.scrollIntoView({ block: 'nearest' });
    const focusable = bodyRef.current.querySelector(
      'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])'
    );
    if (focusable) focusable.focus();
  }, [owns, open, activePath]);

  const headingId = `question-panel-${panel}-heading`;
  const bodyId = `question-panel-${panel}-body`;

  return (
    <Panel aria-labelledby={headingId}>
      <Head type="button" id={headingId} aria-expanded={open} aria-controls={bodyId} onClick={() => togglePanel(panel)}>
        <Caret $open={open} aria-hidden="true">
          ▶
        </Caret>
        <Num aria-hidden="true">{index}</Num>
        <HeadText>
          <Body5 bold>{title}</Body5>
          <Container top={4}>
            <Body7 isLowContrast>{summary}</Body7>
          </Container>
        </HeadText>
        <HeadMeta>
          {seedOrigin ? (
            <Chip title={`${changed} path${changed === 1 ? '' : 's'} differ from the spec this was duplicated from`}>
              {changed === 0 ? 'unchanged' : `${changed} changed`}
            </Chip>
          ) : null}
          <Chip $tone={errors ? 'bad' : undefined}>
            {errors} {errors === 1 ? 'problem' : 'problems'}
          </Chip>
        </HeadMeta>
      </Head>

      {open && (
        <Bodywrap id={bodyId}>
          <Container bottom={16} top={16}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <ModeToggle role="group" aria-label={`${title} editing mode`}>
                <button type="button" aria-pressed={mode === 'form'} onClick={() => setPanelMode(panel, 'form')}>
                  Form
                </button>
                <button type="button" aria-pressed={mode === 'raw'} onClick={() => setPanelMode(panel, 'raw')}>
                  {'{ } JSON'}
                </button>
              </ModeToggle>
              {onRevertSection && (
                <TransparentFlatButton onClick={onRevertSection}>Revert this section</TransparentFlatButton>
              )}
            </div>
          </Container>

          {/* The ref sits INSIDE the chrome so a [Go] lands on a real control rather than on the
              Form / { } toggle, which is the first focusable element in the panel. */}
          <div ref={bodyRef}>{mode === 'raw' ? <RawPane panel={panel} sectionKey={sectionKey} /> : children}</div>
        </Bodywrap>
      )}
    </Panel>
  );
};
